import { useEffect, useRef } from 'react';

const solutions = [
  {
    num: '01',
    icon: 'ri-shield-check-fill',
    title: '실리콘 패킹 일체형 구조',
    desc: '고성능 실리콘 패킹이 마감캡 본체에 일체화되어 있어, 별도의 실란트 도포 없이 볼트 체결만으로 완벽한 방수가 구현됩니다.',
    features: ['내구성 실리콘 소재', '일체형 결합', '추가 실리콘 작업 불필요'],
    image: 'https://storage.readdy-site.link/project_files/a91d34d6-979b-40c4-a28b-dffad304fbfe/e887b210-812b-4abe-bc08-5d1f4401ce40_1-Photoroom-.png?v=a021eaecc99655c9822a066d80b409b5',
  },
  {
    num: '02',
    icon: 'ri-water-flash-fill',
    title: '자동 누수 차단 시스템',
    desc: '볼트 조임과 동시에 실리콘 패킹이 균일하게 압착되어 완벽한 밀폐 구조를 형성합니다. 우수한 탄성으로 온도 변화에도 방수 성능이 유지됩니다.',
    features: ['균일 압착 메커니즘', '온도 변화 대응', '탁월한 방수 보증'],
    image: 'https://storage.readdy-site.link/project_files/a91d34d6-979b-40c4-a28b-dffad304fbfe/9eb98c32-797d-4a61-a839-e361ed0eecda_2-Photoroom-.png?v=ee390a0063c28e925daf07171be27959',
  },
  {
    num: '03',
    icon: 'ri-settings-3-fill',
    title: '간편 원스텝 시공',
    desc: '복잡한 2단계 작업이 사라지고 일반 마감캡과 동일한 방식으로 시공 가능합니다. 숙련도에 관계없이 누구나 균일한 품질로 시공할 수 있습니다.',
    features: ['1단계 시공 완료', '숙련도 무관한 품질', '실리콘 작업 불필요'],
    image: 'https://storage.readdy-site.link/project_files/a91d34d6-979b-40c4-a28b-dffad304fbfe/0d48e47f-7e41-45e7-89a5-2bdd18c4407d_3-Photoroom-.png?v=b6e7e0819326de49241ee1a33e892da9',
  },
];

const SolutionSection = () => {
  const refs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('opacity-100', 'translate-y-0');
        });
      },
      { threshold: 0.1 }
    );
    refs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="solution" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-[#FF6B35] text-sm font-semibold tracking-widest uppercase">Solution</span>
          <h2 className="mt-3 text-3xl lg:text-4xl font-bold text-[#1B2A4A] font-display">
            스마트캡 핵심 기술 3가지
          </h2>
          <p className="mt-4 text-gray-500 text-base max-w-xl mx-auto leading-relaxed">
            특허 기술로 구현된 혁신적인 방수 솔루션
          </p>
          <div className="mt-4 w-12 h-1 bg-[#FF6B35] mx-auto rounded-full" />
        </div>

        <div className="flex flex-col gap-16">
          {solutions.map((item, i) => (
            <div
              key={i}
              ref={(el) => { refs.current[i] = el; }}
              className={`flex flex-col lg:flex-row items-center gap-10 opacity-0 translate-y-8 transition-all duration-600 ${i % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
              style={{ transitionDelay: `${i * 150}ms` }}
            >
              <div className="flex-1 w-full">
                <div className="relative rounded-2xl overflow-hidden bg-[#F5F6F8] flex items-center justify-center" style={{ minHeight: '280px' }}>
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-64 lg:h-80 object-contain p-6 drop-shadow-xl transition-transform duration-500 hover:scale-105"
                  />
                  <div className="absolute top-4 left-4 bg-[#1B2A4A] text-[#FF6B35] font-display font-bold text-2xl w-12 h-12 flex items-center justify-center rounded-lg">
                    {item.num}
                  </div>
                </div>
              </div>

              <div className="flex-1">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#FF6B35]/10 mb-4">
                  <i className={`${item.icon} text-xl text-[#FF6B35]`} />
                </div>
                <h3 className="text-2xl font-bold text-[#1B2A4A] mb-4">{item.title}</h3>
                <p className="text-gray-500 leading-relaxed mb-6 text-sm lg:text-base">{item.desc}</p>
                <ul className="flex flex-col gap-2">
                  {item.features.map((f, j) => (
                    <li key={j} className="flex items-center gap-3">
                      <span className="w-5 h-5 flex items-center justify-center rounded-full bg-[#FF6B35] flex-shrink-0">
                        <i className="ri-check-line text-white text-xs" />
                      </span>
                      <span className="text-[#1B2A4A] font-medium text-sm">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SolutionSection;
